import { ISanityVendorConfigs } from '@rbi-ctg/menu';
import {
  ILoyaltyInRestaurantIncentiveInput,
  ILoyaltyInRestaurantOrderLineItem,
  ILoyaltyRbiVendorConfigsInput,
  CartEntryType as LegacyCartEntryTypeGateway,
  LoyaltyCartDiscountType,
} from 'generated/graphql-gateway';
import { IOfferDiscount } from 'generated/sanity-graphql';
import { isCombo, isItem, isPicker } from 'utils/menu/is-menu-type';
import { blockContentToPlainText } from 'utils/sanity';

import { LoyaltyAppliedOffer } from '../types';

import { ICartEntryType } from './hooks/use-in-restaurant-redemption-cart';
import type {
  ICart,
  ICartEntryOffer,
  ICartEntryReward,
  ILegacyCartEntry,
} from './hooks/use-in-restaurant-redemption-cart';

// the accepted BK gateway vendors
const lineItemVendors = [
  'brink',
  'brinkDelivery',
  'carrols',
  'carrolsDelivery',
  'ncr',
  'ncrDelivery',
  'qdi',
  'qdiDelivery',
  'sicom',
  'sicomDelivery',
];

// filter invalid vendors
export const filterVendorConfigs = (vendorConfig: ISanityVendorConfigs) =>
  Object.keys(vendorConfig).reduce((accum, vendorKey) => {
    if (lineItemVendors.includes(vendorKey)) {
      accum[vendorKey] = vendorConfig[vendorKey];
    }

    return accum;
  }, {});

const getFilterBenefitsVendorConfigs = <IncentiveObject extends readonly (object | null)[]>(
  incentiveBenefits: IncentiveObject | null | undefined
) => {
  return incentiveBenefits?.reduce<Array<ILoyaltyRbiVendorConfigsInput>>((acc, incentive) => {
    if (incentive && (isItem(incentive) || isCombo(incentive)) && incentive?.vendorConfigs) {
      acc.push(filterVendorConfigs(incentive.vendorConfigs));
    } else if (isPicker(incentive)) {
      // if picker collect all options' vendor configs
      const pickerBenefitVendorConfigs = getFilterBenefitsVendorConfigs(
        incentive.options.map(({ option }) => option)
      );

      if (pickerBenefitVendorConfigs?.length) {
        acc.push(...pickerBenefitVendorConfigs);
      }
    }

    return acc;
  }, []);
};

const getIncentiveVendorConfigs = (
  cartEntry: ILegacyCartEntry
): { incentiveVendorConfigs: Array<ILoyaltyRbiVendorConfigsInput> } | null => {
  const incentives =
    cartEntry.type === ICartEntryType.OFFER
      ? cartEntry.details.offer.incentives
      : cartEntry.details.reward.incentives;

  const incentiveVendorConfigs:
    | Array<ILoyaltyRbiVendorConfigsInput>
    | undefined = getFilterBenefitsVendorConfigs(incentives);

  return incentiveVendorConfigs?.length ? { incentiveVendorConfigs } : null;
};

const getNameFromCartEntry = (offerCartEntry: ICartEntryOffer): string => {
  const { offer } = offerCartEntry.details;
  const stringOfferName = blockContentToPlainText(offer?.name?.localeRaw || []);

  return stringOfferName;
};

const getLocalizedPropFromCartEntry = (
  cartEntry: ICartEntryOffer,
  propName: 'name' | 'description'
): Pick<ILoyaltyInRestaurantIncentiveInput, 'name' | 'description'> | null => {
  if (!propName) {
    return null;
  }
  const offer = cartEntry.details.offer;
  const stringOffer = blockContentToPlainText(offer[propName]?.localeRaw || []);

  return { [propName]: stringOffer };
};

const rewardOrderLineItem = (cartEntry: ICartEntryReward): ILoyaltyInRestaurantOrderLineItem => {
  const { quantity, referenceId, type } = cartEntry;
  const { engineReward, reward } = cartEntry.details;

  return {
    incentive: {
      id: engineReward.id,
      ...getIncentiveVendorConfigs(cartEntry),
      type,
    },
    productId: reward._id,
    quantity,
    referenceId,
    vendorConfigs: filterVendorConfigs(reward.vendorConfigs as ISanityVendorConfigs),
  };
};

const offerOrderLineItem = (cartEntry: ICartEntryOffer): ILoyaltyInRestaurantOrderLineItem => {
  const { quantity, referenceId, type } = cartEntry;
  const { offer } = cartEntry.details;
  const { shortCode: offerCode } = offer;
  let id: string;

  if (cartEntry.type === ICartEntryType.OFFER) {
    id = cartEntry.details.offer.loyaltyEngineId!;
  } else {
    id = cartEntry.details.offer._id;
  }
  const name = getNameFromCartEntry(cartEntry);

  const offerDiscountIncentive =
    'incentives' in offer &&
    (offer.incentives?.find(
      incentive => incentive?.__typename === LegacyCartEntryTypeGateway.OFFERDISCOUNT
    ) as IOfferDiscount);

  return {
    incentive: {
      id,
      ...getLocalizedPropFromCartEntry(cartEntry, 'name'),
      ...getLocalizedPropFromCartEntry(cartEntry, 'description'),
      ...getIncentiveVendorConfigs(cartEntry),
      name,
      type,
      ...(offerCode && { offerCode }),
      ...(offerDiscountIncentive && {
        discount: {
          type:
            offerDiscountIncentive.discountType?.toUpperCase() ===
            LoyaltyCartDiscountType.PERCENTAGE
              ? LoyaltyCartDiscountType.PERCENTAGE
              : LoyaltyCartDiscountType.AMOUNT,
          value: offerDiscountIncentive.discountValue || 0,
        },
      }),
    },
    quantity,
    productId: offer._id,
    referenceId,
    vendorConfigs: filterVendorConfigs(offer.vendorConfigs as ISanityVendorConfigs),
  };
};

const generateOrderLineItemsMap: {
  [key in ICartEntryType]: (ce: ILegacyCartEntry) => ILoyaltyInRestaurantOrderLineItem;
} = {
  [ICartEntryType.REWARD]: rewardOrderLineItem,
  [ICartEntryType.OFFER]: offerOrderLineItem,
  [ICartEntryType.LEGACY_OFFER]: offerOrderLineItem,
};

export const generateOrderLineItems = (cart: ICart): Array<ILoyaltyInRestaurantOrderLineItem> =>
  cart.map((cartEntry: ILegacyCartEntry) => generateOrderLineItemsMap[cartEntry.type](cartEntry));

export const getAppliedOffersFromInRestaurantCart = (
  inRestaurantCart: ICart
): LoyaltyAppliedOffer[] => {
  return inRestaurantCart.reduce<LoyaltyAppliedOffer[]>((acc: LoyaltyAppliedOffer[], cartEntry) => {
    if (cartEntry.type === ICartEntryType.OFFER) {
      const { offer } = cartEntry.details;
      acc.push({
        id: offer.loyaltyEngineId,
        cartId: offer._id,
        isStackable: offer.isStackable,
        cmsId: offer._id,
      });
    }
    return acc;
  }, []);
};

// Polling interval when the user is in a different screen, in milliseconds
export const NOT_IN_MY_CODE_SCREEN_POLLING_INTERVAL_MS = 60000;
